import { WEAPON_DAMAGE_MULTIPLIER, type DamageType } from '../config/constants';
import type { EnemyDef } from '../data/enemies';

export interface DamageEvent {
  sourceId: string;
  targetId: string;
  baseDamage: number;
  damageType: DamageType;
  critical: boolean;
  armourPen: number;
  buffMul: number;
  resistMul: number;
  final: number;
}

export function resistMultiplier(def: EnemyDef, type: DamageType): number {
  return def.resist[type] ?? 1;
}

export function computeDamage(
  base: number,
  type: DamageType,
  def: EnemyDef,
  armour: number,
  armourPen: number,
  critChance: number,
  buffMul = 1,
  weaponDamageMul = 1,
): DamageEvent {
  const crit = Math.random() < critChance;
  const critMul = crit ? 1.75 : 1;
  const effectiveArmour = armour * (1 - Math.min(0.9, armourPen));
  const resist = resistMultiplier(def, type);
  // Soft armour: each point reduces damage by ~2% of base, floored
  const armourMul = Math.max(0.2, 1 - effectiveArmour * 0.018);
  const final = Math.max(1, Math.round(base * WEAPON_DAMAGE_MULTIPLIER * weaponDamageMul * critMul * resist * armourMul * buffMul));
  return {
    sourceId: '',
    targetId: '',
    baseDamage: base,
    damageType: type,
    critical: crit,
    armourPen,
    buffMul,
    resistMul: resist,
    final,
  };
}

export type StatusKind = 'BURN' | 'SLOW' | 'STUN' | 'ARMOUR_BREAK' | 'SHIELD' | 'DAMAGE_AMP';

export interface StatusEffect {
  kind: StatusKind;
  duration: number;
  magnitude: number;
  sourceId: string;
}

export function applyStatus(list: StatusEffect[], next: StatusEffect): void {
  if (next.kind === 'SLOW') next.magnitude = Math.min(0.95, Math.max(0, next.magnitude));
  const existing = list.find((s) => s.kind === next.kind && s.sourceId === next.sourceId);
  if (existing) {
    existing.duration = Math.max(existing.duration, next.duration);
    existing.magnitude = Math.max(existing.magnitude, next.magnitude);
    return;
  }
  // Cap stack kinds
  const count = list.filter((s) => s.kind === next.kind).length;
  if (count >= 3) return;
  list.push({ ...next });
}

export function tickStatuses(list: StatusEffect[], dt: number): StatusEffect[] {
  for (const s of list) s.duration -= dt;
  return list.filter((s) => s.duration > 0);
}

export function statusMul(list: StatusEffect[], kind: StatusKind): number {
  const hit = list.find((s) => s.kind === kind);
  return hit ? hit.magnitude : 0;
}

export function hasStatus(list: StatusEffect[], kind: StatusKind): boolean {
  return list.some((s) => s.kind === kind);
}
